import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { IconChevronDownSvg, IconCloseSmallSvg } from '../../../assets/achievements';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	achievementsGlow,
	FILTER_ACTIVE_FILL,
	FILTER_FILL,
	RADIUS_MD,
} from './achievementsTheme';
import { AchievementCategoryOption, achievementText } from './achievementsViewModel';

/**
 * Figma "Achievement filters" (4488:19631): All / Unlocked / Locked and a Categories pill with the
 * "Icon / Chevron Down" the file uses for dropdown-style tabs. 334 wide, 34 tall, 6 between.
 *
 * Section "05 · Components & tokens" (4488:47976) draws the Categories chip in both of its states:
 * "Menu trigger", the resting plate with the chevron, and "Value applied", the active red plate
 * carrying the chosen category and an "Icon / Close Small" (4428:3717) that clears it. The menu
 * itself is `CategoriesDropdown`, which the screen anchors under this row.
 */
export type AchievementFilterKey = 'all' | 'unlocked' | 'locked';

export const FILTERS_WIDTH = 334;
export const FILTERS_HEIGHT = 34;
const GAP = 6;
const PILL_WIDTH = 72;
const CATEGORIES_WIDTH = 100;
const CHEVRON_SIZE = 18;

const FILTERS: Array<{ key: AchievementFilterKey; labelKey: string }> = [
	{ key: 'all', labelKey: 'T_SITE_ACHIEVEMENTS_FILTER_ALL' },
	{ key: 'unlocked', labelKey: 'T_SITE_ACHIEVEMENTS_FILTER_UNLOCKED' },
	{ key: 'locked', labelKey: 'T_SITE_ACHIEVEMENTS_FILTER_LOCKED' },
];

type Props = {
	value: AchievementFilterKey;
	onChange: (filter: AchievementFilterKey) => void;
	/** The applied category, if the dropdown has one. */
	category?: AchievementCategoryOption;
	menuOpen: boolean;
	onToggleMenu: () => void;
	onClearCategory: () => void;
	scale: number;
};

export const AchievementFilters = ({
	value,
	onChange,
	category,
	menuOpen,
	onToggleMenu,
	onClearCategory,
	scale,
}: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const activeFillId = useSvgId('achievementFilterActive');
	const restFillId = useSvgId('achievementFilterRest');

	const plate = (active: boolean) => (
		<View pointerEvents='none' style={StyleSheet.absoluteFill}>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={activeFillId} x1='0' y1='0' x2='0' y2='1'>
						{FILTER_ACTIVE_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
					<LinearGradient id={restFillId} x1='0' y1='0' x2='0' y2='1'>
						{FILTER_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect
					x='0'
					y='0'
					width='100%'
					height='100%'
					fill={`url(#${active ? activeFillId : restFillId})`}
				/>
			</Svg>
		</View>
	);

	return (
		<View style={styles.row}>
			{FILTERS.map(filter => {
				const isActive = filter.key === value;

				return (
					<Pressable
						key={filter.key}
						accessibilityRole='tab'
						accessibilityState={{ selected: isActive }}
						onPress={() => onChange(filter.key)}
						style={[styles.pill, isActive ? styles.pillActive : styles.pillRest]}
					>
						{plate(isActive)}
						<Text
							numberOfLines={1}
							style={[styles.label, isActive ? styles.labelActive : styles.labelRest]}
						>
							{achievementText(t, filter.labelKey)}
						</Text>
					</Pressable>
				);
			})}

			<Pressable
				accessibilityRole='button'
				accessibilityState={{ expanded: menuOpen, selected: !!category }}
				onPress={onToggleMenu}
				style={[
					styles.pill,
					styles.categories,
					category ? styles.pillActive : styles.pillRest,
					category ? styles.categoriesApplied : null,
				]}
			>
				{plate(!!category)}
				<Text
					numberOfLines={1}
					style={[styles.label, category ? styles.labelActive : styles.labelRest]}
				>
					{category
						? t(category.nameKey)
						: achievementText(t, 'T_SITE_ACHIEVEMENTS_FILTER_CATEGORIES')}
				</Text>
				{/* Wrapped for the same painting-order reason as the inventory tabs' icons. */}
				{category ? (
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={achievementText(t, 'T_SITE_ACHIEVEMENTS_CLEAR_CATEGORY')}
						onPress={onClearCategory}
						hitSlop={8}
					>
						<IconCloseSmallSvg width={CHEVRON_SIZE * scale} height={CHEVRON_SIZE * scale} />
					</Pressable>
				) : (
					<View>
						<IconChevronDownSvg width={CHEVRON_SIZE * scale} height={CHEVRON_SIZE * scale} />
					</View>
				)}
			</Pressable>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			flexDirection: 'row',
			width: FILTERS_WIDTH * scale,
			height: FILTERS_HEIGHT * scale,
			gap: GAP * scale,
		},
		pill: {
			width: PILL_WIDTH * scale,
			height: FILTERS_HEIGHT * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: GAP * scale,
			paddingHorizontal: 4 * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(1, 1 * scale),
			overflow: 'hidden',
		},
		categories: {
			width: CATEGORIES_WIDTH * scale,
		},
		// The "Value applied" chip carries a little more side padding than the plain filters.
		categoriesApplied: {
			paddingHorizontal: 6 * scale,
		},
		pillRest: {
			borderColor: achievementsColors.border,
		},
		pillActive: {
			borderColor: achievementsColors.borderActive,
			...achievementsGlow.filterActive,
		},
		// Figma "DS/Label/M": Sofia Sans SemiBold 12, line-height 1.2.
		label: {
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.2 * scale,
			textAlign: 'center',
		},
		labelRest: {
			color: achievementsColors.secondary,
		},
		labelActive: {
			color: achievementsColors.primary,
		},
	});

export default AchievementFilters;
